import React, { FC, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Fade,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSubmitConsentMutation, healthWalletApi } from './healthWalletSlice';
import DrawIcon from '@mui/icons-material/Draw';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import {  useAppDispatch } from '../../app/store';
import { useSelector } from 'react-redux';
import { getOnboardingStatus } from './onboardingUtils';
import { baseName } from '../..';
//import { useOnboarding } from './OnboardingContext';

interface OnboardingConsentProps {
  onNext: () => void;
  onBack?: () => void;
}

const CONSENT_VERSION = '1.1.0';

// The full HIPAA consent document (DiMe-Society-based) is served as a static file from /public
// and fetched ON EXPAND — never bundled into the JS. See OnboardingConsent below.
const CONSENT_DOC_PATH = `${process.env.PUBLIC_URL}/HIPAA_Consent-Form-1.htm`;

export const OnboardingConsent: FC<OnboardingConsentProps> = ({ onNext, onBack }) => {
  const [typedName, setTypedName] = useState('');
  // One consent box now covers BOTH backend keys (terms_of_use + fhir_network_consent) — the user agrees to the
  // same two things (ToU + electronic access, and FHIR/QHIN network use), just in a single checkbox.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Optional Merck research-study opt-in. Default-checked ONLY when the app was entered via
  // /map/bladder-cancer/bcanstudy (session Redux flag) — never gates submit, only ever sent as `true`.
  const [agreedToMerckStudy, setAgreedToMerckStudy] = useState(false);
  // The Merck/BCAN study is bladder-cancer-only — the opt-in is neither shown nor submitted on other maps.
  const isBladderCancerMap = baseName === 'bladder-cancer';
  // Optional Research Connect Direct opt-in — the INVERSE of the Merck box: every map EXCEPT bladder cancer.
  // Default-checked only for /researchconnect entrants; same only-ever-`true` submit rule as merck_study.
  const [agreedToResearchConnect, setAgreedToResearchConnect] = useState(false);
  const [showFullConsent, setShowFullConsent] = useState(false);
  const [submitConsent, { isLoading, isError, error }] = useSubmitConsentMutation();
  const dispatch = useAppDispatch();
  const enteredViaBcanStudy = useSelector((s: any) => s.dialog.bcanStudy);
  const enteredViaResearchConnect = useSelector((s: any) => s.dialog.researchConnect);

  // Full consent doc: fetched from /public on FIRST expand (lazy — never in the JS bundle) and
  // rendered in a Shadow DOM so the Word-exported HTML keeps its formatting AND its styles are
  // isolated from the app (no bleed either way). QS-1 wording lives in the .htm, not here.
  const consentHostRef = useRef<HTMLDivElement>(null);
  const [consentHtml, setConsentHtml] = useState<string | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState(false);
  
  // 🆕 Use onboarding context for status
  //const { onboardingStatus } = useOnboarding();
 const onboardingStatus = getOnboardingStatus()
  
  useEffect(() => {
    if (onboardingStatus?.consents) {
      // Access consent types from the nested structure
      const consentData = onboardingStatus.consents;
      setAgreedToTerms(consentData.terms_of_use?.granted || false);
      
      // Pre-fill signature from the correct nested location
      // Extract only string values for signature
      const termsSignature = typeof consentData.terms_of_use?.signatureData === 'string' 
        ? consentData.terms_of_use?.signatureData : null;
      const fhirSignature = typeof consentData.fhir_network_consent?.signatureData === 'string'
        ? consentData.fhir_network_consent?.signatureData : null;
      const mainSignature = typeof consentData.signatureData === 'string'
        ? consentData.signatureData : null;
      
      const existingSignature = termsSignature || fhirSignature || mainSignature;
      if (existingSignature) {
        setTypedName(existingSignature);
      }
    }
  }, [onboardingStatus]);

  // Default the optional Merck-study opt-in: honor a prior server answer if one exists, otherwise
  // pre-check it only for users who entered via /map/bladder-cancer/bcanstudy. (merck_study is only ever
  // stored as `true`, so a server value here is always `true` when present.)
  useEffect(() => {
    const serverGranted = onboardingStatus?.consents?.merck_study?.granted;
    setAgreedToMerckStudy(serverGranted ?? enteredViaBcanStudy);
  }, [onboardingStatus, enteredViaBcanStudy]);

  // Same defaulting rule for Research Connect Direct: prior server answer wins, otherwise pre-check only for
  // users who entered via /researchconnect. (research_connect_direct is only ever stored as `true`.)
  useEffect(() => {
    const serverGranted = onboardingStatus?.consents?.research_connect_direct?.granted;
    setAgreedToResearchConnect(serverGranted ?? enteredViaResearchConnect);
  }, [onboardingStatus, enteredViaResearchConnect]);

  // Lazy-fetch the consent document the first time the user expands it.
  useEffect(() => {
    if (!showFullConsent || consentHtml !== null || consentLoading || consentError) return;
    setConsentLoading(true);
    fetch(CONSENT_DOC_PATH)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then((html) => setConsentHtml(html))
      .catch(() => setConsentError(true))
      .finally(() => setConsentLoading(false));
  }, [showFullConsent, consentHtml, consentLoading, consentError]);

  // Inject the fetched HTML into a Shadow DOM host: preserves the Word <style> block, isolated from the app.
  useEffect(() => {
    const host = consentHostRef.current;
    if (!host || !consentHtml) return;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.innerHTML = consentHtml;
  }, [consentHtml, showFullConsent]);

  const canSubmit = typedName.trim().length >= 2 && agreedToTerms && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const result = await submitConsent({
        signatureData: typedName.trim(),
        consentVersion: CONSENT_VERSION,
        consents: {
          // The single consent checkbox drives BOTH keys — same two consents, one box.
          terms_of_use: agreedToTerms,
          fhir_network_consent: agreedToTerms,
          // Only include when checked — never send `false` (would break the server's every-granted rule).
          // Bladder-cancer map only, so a hidden checkbox can never re-submit the study consent elsewhere.
          ...(isBladderCancerMap && agreedToMerckStudy ? { merck_study: true } : {}),
          // Research Connect Direct — every map EXCEPT bladder cancer, same only-send-`true` rule.
          ...(!isBladderCancerMap && agreedToResearchConnect ? { research_connect_direct: true } : {}),
        },
      }).unwrap();
      
      // Update the RTK cache with the new state
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          Object.assign(draft, result.state);
        })
      );
      
      onNext();
    } catch (err) {
      console.error('Consent submission failed:', err);
    }
  };

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          pt: { xs: '88px', sm: '112px' },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
              <GavelIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8e8e93', letterSpacing: 1.5 }}>
                Step 1 of 4
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Consent & Authorization
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="sm" sx={{ mt: -2, pb: 6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          {/* Summary */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1c1c1e' }}>
              Health Records Access Consent
            </Typography>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { color: BRAND_DARKER } }}>
              By signing below, you authorize Polygon Health to request your medical records on your behalf through certified health information networks, and to use AI to help summarize and answer questions about them.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
              Your consent enables us to:
            </Typography>

            <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Submit FHIR-based data requests through <strong>Kno2</strong>, our certified health information service provider
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Access the <strong>Qualified Health Information Network (QHIN)</strong> to retrieve your records from participating providers
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Aggregate and display your health data securely within your Health Wallet
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Use your data only to serve you — including AI-assisted summaries and answering your questions — never for advertising or sale
              </Typography>
            </Box>

            {/* Expandable full consent text */}
            <Button
              variant="text"
              size="small"
              onClick={() => setShowFullConsent(!showFullConsent)}
              aria-expanded={showFullConsent}
              aria-controls="full-consent-document"
              endIcon={showFullConsent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: BRAND_DARKER, textTransform: 'none', fontWeight: 600, mb: 2 }}
            >
              {showFullConsent ? 'Hide' : 'Read'} Full Consent Document
            </Button>

            <Collapse in={showFullConsent}>
              <Paper id="full-consent-document" elevation={0} sx={{ p: 3, bgcolor: '#f7f7fa', borderRadius: 2, mb: 3, maxHeight: 300, overflowY: 'auto' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Version {CONSENT_VERSION} — Effective Date: {new Date().toLocaleDateString()}
                </Typography>
                {consentLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={18} sx={{ color: BRAND_DARKER }} />
                    <Typography variant="body2" color="text.secondary">Loading consent document…</Typography>
                  </Box>
                )}
                {consentError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    Couldn’t load the consent document.{' '}
                    <a href={CONSENT_DOC_PATH} target="_blank" rel="noopener noreferrer">Open it in a new tab</a>.
                  </Alert>
                )}
                {/* DiMe-based HIPAA consent HTML — fetched from /public on first expand and rendered inside a
                    Shadow DOM (keeps the Word formatting, isolates its styles from the app). The document is the
                    single source of truth; QS-1 "all available records for AI processing" lives in the .htm. */}
                <div ref={consentHostRef} role="document" aria-label="Full HIPAA consent document" />
              </Paper>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            {/* Checkboxes */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{ color: BRAND_DARK, '&.Mui-checked': { color: BRAND_DARKER } }}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to the consent terms above and the{' '}
                  <a href={`${process.env.PUBLIC_URL}/hw-terms-of-use.html`} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_DARKER, fontWeight: 600 }}>Terms of Use</a>, and authorize electronic access to my health records.
                </Typography>
              }
              sx={{ mb: 1, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
            />

            {/* Optional Merck/BCAN research-study opt-in — BLADDER-CANCER MAP ONLY (the study is NMIBC-specific).
                Full consent text lives in public/merck-study-consent.html (linked below), like the Terms of Use.
                Does NOT gate submit; default-checked only for /map/bladder-cancer/bcanstudy entrants. */}
            {isBladderCancerMap && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToMerckStudy}
                    onChange={(e) => setAgreedToMerckStudy(e.target.checked)}
                    sx={{ color: BRAND_DARK, '&.Mui-checked': { color: BRAND_DARKER } }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I authorize Polygon Health to share my health records, with the direct identifiers removed, for
                    the Merck / BCAN bladder cancer (NMIBC) research study, as described in the{' '}
                    <a href={`${process.env.PUBLIC_URL}/merck-study-consent.html`} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_DARKER, fontWeight: 600 }}>Research Data Sharing Consent &amp; Authorization</a>.
                    Participation is optional and I may withdraw at any time.
                  </Typography>
                }
                sx={{ mb: 3, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
              />
            )}

            {/* Optional Research Connect Direct opt-in — EVERY MAP EXCEPT bladder cancer (which has the Merck/BCAN
                study instead). Full consent text lives in public/research-connect-direct-consent.html. Does NOT gate
                submit; default-checked only for /researchconnect entrants. */}
            {!isBladderCancerMap && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToResearchConnect}
                    onChange={(e) => setAgreedToResearchConnect(e.target.checked)}
                    sx={{ color: BRAND_DARK, '&.Mui-checked': { color: BRAND_DARKER } }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I authorize Polygon Health to make my health records, with the direct identifiers removed,
                    available to qualified researchers through <strong>Research Connect Direct</strong>, as described in the{' '}
                    <a href={`${process.env.PUBLIC_URL}/research-connect-direct-consent.html`} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_DARKER, fontWeight: 600 }}>Research Connect Direct Consent &amp; Authorization</a>.
                    Participation is optional, I can see who has viewed my data, and I may withdraw at any time.
                  </Typography>
                }
                sx={{ mb: 3, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
              />
            )}

            {/* Signature field */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1c1c1e' }}>
              Digital Signature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Type your full legal name below to digitally sign this consent.
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your full name"
              label="Full legal name"
              autoComplete="name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              InputProps={{
                startAdornment: <DrawIcon sx={{ color: BRAND_DARK, mr: 1 }} />,
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': { borderColor: '#d7d3f0' },
                  '&:hover fieldset': { borderColor: BRAND_DARKER },
                  '&.Mui-focused fieldset': { borderColor: BRAND_DARKER },
                },
                '& .MuiInputBase-input': {
                  fontFamily: "'Georgia', serif",
                  fontSize: '1.2rem',
                  fontStyle: 'italic',
                },
              }}
            />
            {typedName.trim().length > 0 && typedName.trim().length < 2 && (
              <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                Please enter at least 2 characters.
              </Typography>
            )}

            {/* Confirmation preview */}
            {typedName.trim().length >= 2 && agreedToTerms && (
              <Fade in>
                <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                  <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#166534' }}>
                    Ready to sign as <strong style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic' }}>{typedName.trim()}</strong>
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Error display */}
            {isError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                Failed to submit consent. Please try again. {(error as any)?.data?.message || ''}
              </Alert>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: { xs: 2, md: 4 }, pt: 0, gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                color: BRAND_DARKER,
                borderColor: '#d7d3f0',
                borderRadius: 50,
                textTransform: 'none',
                fontWeight: 600,
                py: 0.75,
                '&:hover': { borderColor: BRAND_DARKER, bgcolor: '#f7f7fa' },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="small"
              endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                bgcolor: BRAND_DARKER,
                borderRadius: 50,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 0.75,
                '&:hover': { bgcolor: '#5a3fc0' },
                '&.Mui-disabled': { bgcolor: '#8a7ac0', color: 'white' },
              }}
            >
              {isLoading ? 'Signing...' : 'Sign & Continue'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
